import { useState, useCallback, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useProjectStore } from '@/store/projectStore';
import { createTextOverlay, createBlurOverlay, createRectangleOverlay, createRoundedRectangleOverlay } from '@/utils/overlayFactory';
import type { OverlayElement } from '@/types';
import clsx from 'clsx';
import {
    GripHorizontal,
    LayoutTemplate,
    X, Sparkles, Save, FolderOpen,
    Youtube, Radio, Shield, Briefcase,
    Newspaper, Trophy, GraduationCap, Home,
} from 'lucide-react';


// ─── Template Definitions ─────────────────────────────────────────────────────

type TemplateCategory = 'YouTube' | 'TikTok' | 'Privacy' | 'Business' | 'Sports' | 'News' | 'Education' | 'Real Estate';

interface TemplateOverlayDef {
    type: 'text' | 'blur' | 'rectangle' | 'rounded-rectangle';
    // Bounds as percentages of video dimensions
    xPct: number; yPct: number; wPct: number; hPct: number;
    // Text props
    content?: string;
    fontSize?: number;
    fontWeight?: 'normal' | 'bold';
    hasBackground?: boolean;
    bgColor?: { r: number; g: number; b: number; a: number };
    fillColor?: { r: number; g: number; b: number; a: number };
    borderColor?: { r: number; g: number; b: number; a: number };
    borderWidth?: number;
    textAlign?: 'left' | 'center' | 'right';
    opacity?: number;
    blurIntensity?: number;
}

interface BuiltInTemplate {
    id: string;
    name: string;
    description: string;
    category: TemplateCategory;
    emoji: string;
    overlays: TemplateOverlayDef[];
}

const BUILT_IN_TEMPLATES: BuiltInTemplate[] = [
    // ── YouTube ──────────────────────────────────────────────────────────────
    {
        id: 'yt-subscribe',
        name: 'Subscribe CTA',
        description: 'Bell + Subscribe reminder',
        category: 'YouTube',
        emoji: '🔔',
        overlays: [
            { type: 'text', xPct: 72, yPct: 88, wPct: 25, hPct: 8,
              content: '🔔 SUBSCRIBE', fontSize: 28, fontWeight: 'bold',
              hasBackground: true, bgColor: { r: 255, g: 0, b: 0, a: 0.85 } },
        ],
    },
    {
        id: 'yt-end-screen',
        name: 'End Screen CTA',
        description: 'Watch Next + Subscribe combo',
        category: 'YouTube',
        emoji: '📺',
        overlays: [
            { type: 'text', xPct: 5, yPct: 80, wPct: 40, hPct: 8,
              content: '▶  Watch Next', fontSize: 22, fontWeight: 'bold',
              hasBackground: true, bgColor: { r: 0, g: 0, b: 0, a: 0.7 } },
            { type: 'text', xPct: 55, yPct: 80, wPct: 40, hPct: 8,
              content: '❤  Subscribe', fontSize: 22, fontWeight: 'bold',
              hasBackground: true, bgColor: { r: 200, g: 0, b: 0, a: 0.8 } },
        ],
    },
    {
        id: 'yt-channel-watermark',
        name: 'Channel Watermark',
        description: '@Channel corner watermark',
        category: 'YouTube',
        emoji: '🎬',
        overlays: [
            { type: 'text', xPct: 70, yPct: 88, wPct: 28, hPct: 7,
              content: '@YourChannel', fontSize: 20, fontWeight: 'bold',
              hasBackground: true, bgColor: { r: 0, g: 0, b: 0, a: 0.5 }, opacity: 70 },
        ],
    },
    {
        id: 'yt-lower-third',
        name: 'Lower Third',
        description: 'Name + Title banner',
        category: 'YouTube',
        emoji: '🎙',
        overlays: [
            { type: 'rounded-rectangle', xPct: 3, yPct: 76, wPct: 50, hPct: 18,
              fillColor: { r: 99, g: 102, b: 241, a: 0.9 }, borderColor: { r: 255, g: 255, b: 255, a: 0 }, borderWidth: 0 },
            { type: 'text', xPct: 5, yPct: 78, wPct: 46, hPct: 7,
              content: 'Your Name', fontSize: 26, fontWeight: 'bold', textAlign: 'left',
              hasBackground: false, fillColor: { r: 255, g: 255, b: 255, a: 1 } },
            { type: 'text', xPct: 5, yPct: 87, wPct: 46, hPct: 6,
              content: 'Title / Description', fontSize: 18, fontWeight: 'normal', textAlign: 'left',
              hasBackground: false, fillColor: { r: 220, g: 220, b: 255, a: 1 } },
        ],
    },

    // ── TikTok / Reels ────────────────────────────────────────────────────────
    {
        id: 'tt-handle',
        name: '@Handle Overlay',
        description: 'Big @handle for TikTok',
        category: 'TikTok',
        emoji: '📱',
        overlays: [
            { type: 'text', xPct: 5, yPct: 55, wPct: 50, hPct: 8,
              content: '@yourhandle', fontSize: 30, fontWeight: 'bold',
              hasBackground: true, bgColor: { r: 0, g: 0, b: 0, a: 0.6 } },
        ],
    },
    {
        id: 'tt-hashtags',
        name: 'Hashtag Pack',
        description: '#FYP #Viral stacked',
        category: 'TikTok',
        emoji: '#️⃣',
        overlays: [
            { type: 'text', xPct: 5, yPct: 65, wPct: 55, hPct: 7,
              content: '#FYP  #Viral  #Trending', fontSize: 20, fontWeight: 'bold',
              hasBackground: true, bgColor: { r: 0, g: 0, b: 0, a: 0.55 } },
        ],
    },
    {
        id: 'tt-side-caption',
        name: 'Side Caption',
        description: 'Vertical text for Reels',
        category: 'TikTok',
        emoji: '📝',
        overlays: [
            { type: 'text', xPct: 5, yPct: 40, wPct: 60, hPct: 20,
              content: 'Your caption\ngoes here', fontSize: 24, fontWeight: 'bold',
              hasBackground: true, bgColor: { r: 0, g: 0, b: 0, a: 0.65 } },
        ],
    },

    // ── Privacy ───────────────────────────────────────────────────────────────
    {
        id: 'priv-face-blur',
        name: 'Face Blur',
        description: 'Blur placeholder — move to face',
        category: 'Privacy',
        emoji: '🙈',
        overlays: [
            { type: 'blur', xPct: 38, yPct: 10, wPct: 24, hPct: 30, blurIntensity: 80 },
        ],
    },
    {
        id: 'priv-plate-blur',
        name: 'Plate Blur',
        description: 'License plate pixelate',
        category: 'Privacy',
        emoji: '🚗',
        overlays: [
            { type: 'blur', xPct: 35, yPct: 70, wPct: 30, hPct: 12, blurIntensity: 90 },
        ],
    },
    {
        id: 'priv-redact-bar',
        name: 'Redact Bar',
        description: 'Black censor bar',
        category: 'Privacy',
        emoji: '🔲',
        overlays: [
            { type: 'rectangle', xPct: 20, yPct: 43, wPct: 60, hPct: 10,
              fillColor: { r: 0, g: 0, b: 0, a: 1 }, borderColor: { r: 0, g: 0, b: 0, a: 0 }, borderWidth: 0 },
        ],
    },

    // ── Business ──────────────────────────────────────────────────────────────
    {
        id: 'biz-copyright',
        name: 'Copyright Footer',
        description: '© 2024 Company Name',
        category: 'Business',
        emoji: '©️',
        overlays: [
            { type: 'text', xPct: 25, yPct: 93, wPct: 50, hPct: 5,
              content: '© 2024 Your Company', fontSize: 16, fontWeight: 'normal',
              hasBackground: false, fillColor: { r: 200, g: 200, b: 200, a: 0.8 }, opacity: 80 },
        ],
    },
    {
        id: 'biz-intro-title',
        name: 'Intro Title',
        description: 'Bold centered title card',
        category: 'Business',
        emoji: '🎯',
        overlays: [
            { type: 'rounded-rectangle', xPct: 10, yPct: 38, wPct: 80, hPct: 24,
              fillColor: { r: 0, g: 0, b: 0, a: 0.75 }, borderColor: { r: 99, g: 102, b: 241, a: 0.8 }, borderWidth: 2 },
            { type: 'text', xPct: 12, yPct: 40, wPct: 76, hPct: 10,
              content: 'Video Title Here', fontSize: 36, fontWeight: 'bold',
              hasBackground: false, fillColor: { r: 255, g: 255, b: 255, a: 1 } },
            { type: 'text', xPct: 12, yPct: 52, wPct: 76, hPct: 7,
              content: 'Subtitle or tagline', fontSize: 20, fontWeight: 'normal',
              hasBackground: false, fillColor: { r: 180, g: 180, b: 220, a: 1 } },
        ],
    },
    {
        id: 'biz-outro',
        name: 'Outro / Follow CTA',
        description: 'Follow us + Link in Bio',
        category: 'Business',
        emoji: '🔗',
        overlays: [
            { type: 'text', xPct: 20, yPct: 75, wPct: 60, hPct: 8,
              content: '👇 Follow us for more', fontSize: 24, fontWeight: 'bold',
              hasBackground: true, bgColor: { r: 99, g: 102, b: 241, a: 0.85 } },
            { type: 'text', xPct: 25, yPct: 85, wPct: 50, hPct: 7,
              content: '🔗 Link in Bio', fontSize: 20, fontWeight: 'normal',
              hasBackground: true, bgColor: { r: 30, g: 30, b: 30, a: 0.7 } },
        ],
    },

    // ── Sports ────────────────────────────────────────────────────────────────
    {
        id: 'sport-score-bug',
        name: 'Score Bug',
        description: 'Home vs Away score display',
        category: 'Sports',
        emoji: '🏆',
        overlays: [
            { type: 'rounded-rectangle', xPct: 1, yPct: 2, wPct: 32, hPct: 10,
              fillColor: { r: 20, g: 20, b: 20, a: 0.9 }, borderColor: { r: 255, g: 255, b: 255, a: 0.2 }, borderWidth: 1 },
            { type: 'text', xPct: 2, yPct: 2, wPct: 15, hPct: 10,
              content: 'HOME\n  0', fontSize: 18, fontWeight: 'bold', textAlign: 'center',
              hasBackground: false, fillColor: { r: 255, g: 255, b: 255, a: 1 } },
            { type: 'text', xPct: 18, yPct: 2, wPct: 14, hPct: 10,
              content: 'AWAY\n  0', fontSize: 18, fontWeight: 'bold', textAlign: 'center',
              hasBackground: false, fillColor: { r: 255, g: 255, b: 255, a: 1 } },
        ],
    },
    {
        id: 'sport-replay',
        name: 'Replay Badge',
        description: '⟳ REPLAY ribbon',
        category: 'Sports',
        emoji: '⟳',
        overlays: [
            { type: 'text', xPct: 68, yPct: 3, wPct: 28, hPct: 9,
              content: '⟳  REPLAY', fontSize: 26, fontWeight: 'bold',
              hasBackground: true, bgColor: { r: 255, g: 165, b: 0, a: 0.9 }, fillColor: { r: 0, g: 0, b: 0, a: 1 } },
        ],
    },

    // ── News ──────────────────────────────────────────────────────────────────
    {
        id: 'news-breaking',
        name: 'Breaking News',
        description: 'Red BREAKING banner',
        category: 'News',
        emoji: '📰',
        overlays: [
            { type: 'rectangle', xPct: 0, yPct: 75, wPct: 100, hPct: 12,
              fillColor: { r: 200, g: 0, b: 0, a: 0.92 }, borderColor: { r: 0, g: 0, b: 0, a: 0 }, borderWidth: 0 },
            { type: 'text', xPct: 2, yPct: 76, wPct: 18, hPct: 10,
              content: '⚡ BREAKING', fontSize: 18, fontWeight: 'bold', textAlign: 'center',
              hasBackground: true, bgColor: { r: 255, g: 220, b: 0, a: 1 }, fillColor: { r: 0, g: 0, b: 0, a: 1 } },
            { type: 'text', xPct: 22, yPct: 77, wPct: 76, hPct: 9,
              content: 'Your headline text goes here', fontSize: 22, fontWeight: 'bold',
              hasBackground: false, fillColor: { r: 255, g: 255, b: 255, a: 1 } },
        ],
    },
    {
        id: 'news-ticker',
        name: 'News Ticker',
        description: 'Bottom scrolling ticker style',
        category: 'News',
        emoji: '📡',
        overlays: [
            { type: 'rectangle', xPct: 0, yPct: 90, wPct: 100, hPct: 8,
              fillColor: { r: 0, g: 40, b: 120, a: 0.95 }, borderColor: { r: 0, g: 0, b: 0, a: 0 }, borderWidth: 0 },
            { type: 'text', xPct: 2, yPct: 91, wPct: 96, hPct: 7,
              content: 'LIVE  ●  Your ticker text scrolls here — more news updates below', fontSize: 18, fontWeight: 'normal',
              hasBackground: false, fillColor: { r: 255, g: 255, b: 255, a: 1 }, textAlign: 'left' },
        ],
    },

    // ── Education ─────────────────────────────────────────────────────────────
    {
        id: 'edu-callout',
        name: 'Key Point Callout',
        description: 'Highlight important info',
        category: 'Education',
        emoji: '💡',
        overlays: [
            { type: 'rounded-rectangle', xPct: 55, yPct: 20, wPct: 42, hPct: 30,
              fillColor: { r: 255, g: 244, b: 0, a: 0.15 }, borderColor: { r: 255, g: 220, b: 0, a: 0.8 }, borderWidth: 2 },
            { type: 'text', xPct: 56, yPct: 21, wPct: 40, hPct: 8,
              content: '💡 Key Point', fontSize: 20, fontWeight: 'bold',
              hasBackground: false, fillColor: { r: 255, g: 220, b: 0, a: 1 } },
            { type: 'text', xPct: 56, yPct: 30, wPct: 40, hPct: 18,
              content: 'Add your important\nnote or tip here', fontSize: 18, fontWeight: 'normal',
              hasBackground: false, fillColor: { r: 255, g: 255, b: 255, a: 0.9 }, textAlign: 'left' },
        ],
    },
    {
        id: 'edu-step-counter',
        name: 'Step Counter',
        description: 'Step 1 / 5 progress badge',
        category: 'Education',
        emoji: '📋',
        overlays: [
            { type: 'rounded-rectangle', xPct: 2, yPct: 2, wPct: 18, hPct: 10,
              fillColor: { r: 99, g: 102, b: 241, a: 0.9 }, borderColor: { r: 255, g: 255, b: 255, a: 0.2 }, borderWidth: 1 },
            { type: 'text', xPct: 2, yPct: 2, wPct: 18, hPct: 10,
              content: 'Step 1 / 5', fontSize: 20, fontWeight: 'bold',
              hasBackground: false, fillColor: { r: 255, g: 255, b: 255, a: 1 } },
        ],
    },

    // ── Real Estate ───────────────────────────────────────────────────────────
    {
        id: 're-price-tag',
        name: 'Property Price',
        description: 'Price + beds/baths tag',
        category: 'Real Estate',
        emoji: '🏠',
        overlays: [
            { type: 'rounded-rectangle', xPct: 60, yPct: 78, wPct: 38, hPct: 18,
              fillColor: { r: 15, g: 15, b: 15, a: 0.88 }, borderColor: { r: 200, g: 160, b: 0, a: 0.7 }, borderWidth: 2 },
            { type: 'text', xPct: 61, yPct: 79, wPct: 36, hPct: 9,
              content: '$1,250,000', fontSize: 26, fontWeight: 'bold',
              hasBackground: false, fillColor: { r: 255, g: 215, b: 0, a: 1 } },
            { type: 'text', xPct: 61, yPct: 89, wPct: 36, hPct: 6,
              content: '4 Bed  ·  3 Bath  ·  2,400 sqft', fontSize: 16, fontWeight: 'normal',
              hasBackground: false, fillColor: { r: 200, g: 200, b: 200, a: 1 }, textAlign: 'left' },
        ],
    },
    {
        id: 're-location',
        name: 'Location Banner',
        description: 'Address + city lower third',
        category: 'Real Estate',
        emoji: '📍',
        overlays: [
            { type: 'rectangle', xPct: 0, yPct: 80, wPct: 60, hPct: 14,
              fillColor: { r: 0, g: 100, b: 60, a: 0.9 }, borderColor: { r: 0, g: 0, b: 0, a: 0 }, borderWidth: 0 },
            { type: 'text', xPct: 2, yPct: 81, wPct: 56, hPct: 7,
              content: '📍 123 Main Street', fontSize: 22, fontWeight: 'bold',
              hasBackground: false, fillColor: { r: 255, g: 255, b: 255, a: 1 }, textAlign: 'left' },
            { type: 'text', xPct: 2, yPct: 89, wPct: 56, hPct: 5,
              content: 'City, State  ·  Listed by Agent Name', fontSize: 15, fontWeight: 'normal',
              hasBackground: false, fillColor: { r: 200, g: 240, b: 210, a: 1 }, textAlign: 'left' },
        ],
    },
];

const CATEGORY_META: Record<TemplateCategory, { icon: React.ReactNode; color: string }> = {
    'YouTube':      { icon: <Youtube size={14} />,      color: '#FF0000' },
    'TikTok':       { icon: <Radio size={14} />,        color: '#69C9D0' },
    'Privacy':      { icon: <Shield size={14} />,       color: '#F59E0B' },
    'Business':     { icon: <Briefcase size={14} />,    color: '#6366F1' },
    'Sports':       { icon: <Trophy size={14} />,       color: '#10B981' },
    'News':         { icon: <Newspaper size={14} />,    color: '#EF4444' },
    'Education':    { icon: <GraduationCap size={14} />,color: '#8B5CF6' },
    'Real Estate':  { icon: <Home size={14} />,         color: '#F97316' },
};

const ALL_CATEGORIES = Object.keys(CATEGORY_META) as TemplateCategory[];

// ─── Save / Load .ostudio format ─────────────────────────────────────────────

interface OStudioFile {
    version: '1.0';
    name: string;
    description?: string;
    createdAt: string;
    overlays: OverlayElement[];
}

// ─── Helper ───────────────────────────────────────────────────────────────────

function calcBounds(
    xPct: number, yPct: number, wPct: number, hPct: number,
    videoW: number, videoH: number
) {
    return {
        x: (xPct / 100) * videoW,
        y: (yPct / 100) * videoH,
        width: (wPct / 100) * videoW,
        height: (hPct / 100) * videoH,
    };
}

// ─── Component ────────────────────────────────────────────────────────────────

export const PresetTemplates = () => {
    const [isOpen, setIsOpen]                     = useState(false);
    const [activeTab, setActiveTab]               = useState<'templates' | 'saved'>('templates');
    const [selectedCategory, setSelectedCategory] = useState<TemplateCategory | 'All'>('All');
    const [savedTemplates, setSavedTemplates]     = useState<OStudioFile[]>([]);
    const [saveName, setSaveName]                 = useState('My Template');
    const [panelPosition, setPanelPosition]       = useState({ x: 0, y: 80 });
    const [isDragging, setIsDragging]             = useState(false);
    const dragStartRef = useRef({ x: 0, y: 0 });
    const panelRef     = useRef<HTMLDivElement>(null);

    const video      = useProjectStore((s) => s.project.video);
    const overlays   = useProjectStore((s) => s.project.overlays);
    const addOverlay = useProjectStore((s) => s.addOverlay);
    const selectOverlay = useProjectStore((s) => s.selectOverlay);
    const deselectAll   = useProjectStore((s) => s.deselectAll);

    // Center panel when opened
    useEffect(() => {
        if (isOpen) setPanelPosition({ x: Math.max(0, window.innerWidth / 2 - 260), y: 80 });
    }, [isOpen]);

    // Load saved templates from localStorage
    useEffect(() => {
        try {
            const raw = localStorage.getItem('overlay_studio_saved_templates');
            if (raw) setSavedTemplates(JSON.parse(raw));
        } catch { /* ignore */ }
    }, [isOpen]);

    // Drag handlers
    const handleMouseDown = useCallback((e: React.MouseEvent) => {
        setIsDragging(true);
        dragStartRef.current = { x: e.clientX - panelPosition.x, y: e.clientY - panelPosition.y };
    }, [panelPosition]);

    useEffect(() => {
        const onMove = (e: MouseEvent) => {
            if (!isDragging) return;
            setPanelPosition({ x: e.clientX - dragStartRef.current.x, y: e.clientY - dragStartRef.current.y });
        };
        const onUp = () => setIsDragging(false);
        if (isDragging) {
            document.addEventListener('mousemove', onMove);
            document.addEventListener('mouseup', onUp);
        }
        return () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
    }, [isDragging]);

    // Apply a built-in template
    const applyTemplate = useCallback((tpl: BuiltInTemplate) => {
        if (!video) return;
        deselectAll();
        const addedIds: string[] = [];

        tpl.overlays.forEach((def, idx) => {
            const bounds = calcBounds(def.xPct, def.yPct, def.wPct, def.hPct, video.width, video.height);

            let overlay: OverlayElement;
            if (def.type === 'blur') {
                const o = createBlurOverlay({ bounds, startTime: 0, endTime: video.duration, zIndex: overlays.length + idx });
                o.blur.intensity = def.blurIntensity ?? 70;
                overlay = o;
            } else if (def.type === 'rectangle') {
                const o = createRectangleOverlay({ bounds, startTime: 0, endTime: video.duration, zIndex: overlays.length + idx });
                if (def.fillColor)   o.style.fillColor   = def.fillColor;
                if (def.borderColor) o.style.borderColor = def.borderColor;
                if (def.borderWidth !== undefined) o.style.borderWidth = def.borderWidth;
                if (def.opacity !== undefined) o.style.opacity = def.opacity;
                overlay = o;
            } else if (def.type === 'rounded-rectangle') {
                const o = createRoundedRectangleOverlay({ bounds, startTime: 0, endTime: video.duration, zIndex: overlays.length + idx });
                if (def.fillColor)   o.style.fillColor   = def.fillColor;
                if (def.borderColor) o.style.borderColor = def.borderColor;
                if (def.borderWidth !== undefined) o.style.borderWidth = def.borderWidth;
                if (def.opacity !== undefined) o.style.opacity = def.opacity;
                overlay = o;
            } else {
                // text
                const o = createTextOverlay({ bounds, startTime: 0, endTime: video.duration, content: def.content, zIndex: overlays.length + idx });
                if (def.fontSize  !== undefined) o.text.fontSize  = def.fontSize;
                if (def.fontWeight) o.text.fontWeight = def.fontWeight;
                if (def.textAlign)  o.text.textAlign  = def.textAlign;
                if (def.hasBackground !== undefined) o.text.hasBackground = def.hasBackground;
                if (def.bgColor)    o.text.backgroundColor = def.bgColor;
                if (def.fillColor)  o.style.fillColor = def.fillColor;
                if (def.opacity !== undefined) o.style.opacity = def.opacity;
                overlay = o;
            }

            addOverlay(overlay);
            addedIds.push(overlay.id);
        });

        // Select first overlay
        if (addedIds.length > 0) selectOverlay(addedIds[0]);
        setIsOpen(false);
    }, [video, overlays, addOverlay, selectOverlay, deselectAll]);

    // Apply a saved template
    const applySavedTemplate = useCallback((file: OStudioFile) => {
        if (!video) return;
        file.overlays.forEach((o, idx) => {
            addOverlay({ ...o, id: crypto.randomUUID(), zIndex: overlays.length + idx });
        });
        setIsOpen(false);
    }, [video, overlays, addOverlay]);

    // Save current overlays as template
    const saveCurrentAsTemplate = useCallback(() => {
        if (!overlays.length) return;
        const file: OStudioFile = {
            version: '1.0',
            name: saveName || 'My Template',
            createdAt: new Date().toISOString(),
            overlays: JSON.parse(JSON.stringify(overlays)),
        };
        const updated = [...savedTemplates, file];
        setSavedTemplates(updated);
        try { localStorage.setItem('overlay_studio_saved_templates', JSON.stringify(updated)); } catch { /* ignore */ }
        setSaveName('My Template');
    }, [overlays, saveName, savedTemplates]);

    // Export template as .ostudio file
    const exportTemplate = useCallback((file: OStudioFile) => {
        const json = JSON.stringify(file, null, 2);
        const blob = new Blob([json], { type: 'application/json' });
        const url  = URL.createObjectURL(blob);
        const a    = document.createElement('a');
        a.href     = url;
        a.download = `${file.name.replace(/\s+/g, '_')}.ostudio`;
        a.click();
        URL.revokeObjectURL(url);
    }, []);

    // Import .ostudio file
    const importOStudio = useCallback(() => {
        const input = document.createElement('input');
        input.type   = 'file';
        input.accept = '.ostudio,.json';
        input.onchange = async (e) => {
            const file = (e.target as HTMLInputElement).files?.[0];
            if (!file) return;
            try {
                const text = await file.text();
                const parsed: OStudioFile = JSON.parse(text);
                if (!parsed.overlays) return;
                const named: OStudioFile = { ...parsed, name: parsed.name || file.name.replace(/\.[^.]+$/, '') };
                const updated = [...savedTemplates, named];
                setSavedTemplates(updated);
                localStorage.setItem('overlay_studio_saved_templates', JSON.stringify(updated));
            } catch { alert('Invalid .ostudio file'); }
        };
        input.click();
    }, [savedTemplates]);

    // Delete saved template
    const deleteSaved = useCallback((idx: number) => {
        const updated = savedTemplates.filter((_, i) => i !== idx);
        setSavedTemplates(updated);
        try { localStorage.setItem('overlay_studio_saved_templates', JSON.stringify(updated)); } catch { /* ignore */ }
    }, [savedTemplates]);

    if (!video) return null;

    const filteredTemplates = selectedCategory === 'All'
        ? BUILT_IN_TEMPLATES
        : BUILT_IN_TEMPLATES.filter((t) => t.category === selectedCategory);

    // ─── Styles ───────────────────────────────────────────────────────────────

    const panelStyle: React.CSSProperties = {
        position: 'fixed',
        top: panelPosition.y,
        left: panelPosition.x,
        width: '520px',
        maxHeight: '80vh',
        backgroundColor: 'rgba(20, 20, 38, 0.93)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        borderRadius: '14px',
        boxShadow: '0 30px 60px -12px rgba(0,0,0,0.85)',
        zIndex: 99999,
        overflow: 'hidden',
        border: '1px solid rgba(99,102,241,0.3)',
        display: 'flex',
        flexDirection: 'column',
    };

    const sectionLabel: React.CSSProperties = {
        fontSize: '10px', color: '#9ca3af', textTransform: 'uppercase',
        letterSpacing: '0.07em', marginBottom: '8px', fontWeight: 600,
    };

    return (
        <div className="relative">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className={clsx('btn btn-secondary text-sm flex items-center gap-2', isOpen && 'ring-2 ring-editor-accent')}
                title="Template Library"
            >
                <Sparkles size={16} />
                <span>Templates</span>
            </button>

            {isOpen && createPortal(
                <>
                    {/* Backdrop */}
                    <div
                        onClick={() => setIsOpen(false)}
                        style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.55)', zIndex: 99998 }}
                    />

                    {/* Panel */}
                    <div ref={panelRef} style={panelStyle}>

                        {/* ── Header ── */}
                        <div
                            onMouseDown={handleMouseDown}
                            style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                padding: '12px 16px',
                                backgroundColor: 'rgba(30, 30, 55, 0.95)',
                                borderBottom: '1px solid rgba(99,102,241,0.25)',
                                cursor: isDragging ? 'grabbing' : 'grab',
                                userSelect: 'none', flexShrink: 0,
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <GripHorizontal size={16} style={{ color: '#6b7280' }} />
                                <LayoutTemplate size={18} style={{ color: '#818cf8' }} />
                                <span style={{ fontWeight: 700, color: '#fff', fontSize: '15px' }}>Template Library</span>
                                <span style={{ fontSize: '11px', color: '#818cf8', background: 'rgba(99,102,241,0.2)', borderRadius: '4px', padding: '1px 6px' }}>
                                    {BUILT_IN_TEMPLATES.length} built-in
                                </span>
                            </div>
                            <button onClick={() => setIsOpen(false)} style={{ padding: '4px', background: 'none', border: 'none', cursor: 'pointer' }}>
                                <X size={16} style={{ color: '#9ca3af' }} />
                            </button>
                        </div>

                        {/* ── Tabs ── */}
                        <div style={{ display: 'flex', borderBottom: '1px solid rgba(99,102,241,0.2)', flexShrink: 0 }}>
                            {(['templates', 'saved'] as const).map((tab) => (
                                <button
                                    key={tab}
                                    onClick={() => setActiveTab(tab)}
                                    style={{
                                        flex: 1, padding: '10px', fontSize: '13px', fontWeight: 600,
                                        background: 'none', border: 'none', cursor: 'pointer',
                                        color: activeTab === tab ? '#818cf8' : '#9ca3af',
                                        borderBottom: activeTab === tab ? '2px solid #6366f1' : '2px solid transparent',
                                    }}
                                >
                                    {tab === 'templates' ? '✨ Built-in' : `📁 Saved (${savedTemplates.length})`}
                                </button>
                            ))}
                        </div>

                        {/* ── Scrollable Content ── */}
                        <div style={{ overflowY: 'auto', flex: 1 }}>

                            {/* ── TEMPLATES TAB ── */}
                            {activeTab === 'templates' && (
                                <div style={{ padding: '16px' }}>
                                    {/* Category filter */}
                                    <p style={sectionLabel}>Category</p>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '16px' }}>
                                        {(['All', ...ALL_CATEGORIES] as const).map((cat) => {
                                            const meta = cat === 'All' ? null : CATEGORY_META[cat];
                                            const active = selectedCategory === cat;
                                            return (
                                                <button
                                                    key={cat}
                                                    onClick={() => setSelectedCategory(cat)}
                                                    style={{
                                                        display: 'flex', alignItems: 'center', gap: '4px',
                                                        padding: '4px 10px', borderRadius: '20px', fontSize: '12px',
                                                        fontWeight: active ? 700 : 400,
                                                        background: active ? (meta?.color ?? '#6366f1') : 'rgba(255,255,255,0.08)',
                                                        border: 'none', cursor: 'pointer',
                                                        color: active ? '#fff' : '#ccc',
                                                    }}
                                                >
                                                    {meta?.icon}
                                                    {cat}
                                                </button>
                                            );
                                        })}
                                    </div>

                                    {/* Templates grid */}
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                                        {filteredTemplates.map((tpl) => {
                                            const meta = CATEGORY_META[tpl.category];
                                            return (
                                                <button
                                                    key={tpl.id}
                                                    onClick={() => applyTemplate(tpl)}
                                                    style={{
                                                        display: 'flex', alignItems: 'flex-start', gap: '10px',
                                                        padding: '12px', borderRadius: '10px', textAlign: 'left',
                                                        background: 'rgba(255,255,255,0.05)',
                                                        border: '1px solid rgba(255,255,255,0.08)',
                                                        cursor: 'pointer',
                                                        transition: 'background 0.15s',
                                                    }}
                                                    onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(99,102,241,0.15)')}
                                                    onMouseLeave={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.05)')}
                                                >
                                                    {/* Emoji thumb */}
                                                    <div style={{
                                                        width: '40px', height: '40px', flexShrink: 0, borderRadius: '8px',
                                                        background: meta.color + '25',
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                        fontSize: '20px',
                                                    }}>
                                                        {tpl.emoji}
                                                    </div>
                                                    <div style={{ flex: 1, minWidth: 0 }}>
                                                        <p style={{ fontSize: '13px', fontWeight: 600, color: '#fff', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                            {tpl.name}
                                                        </p>
                                                        <p style={{ fontSize: '11px', color: '#9ca3af', margin: '2px 0 0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                            {tpl.description}
                                                        </p>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '4px', color: meta.color, fontSize: '11px' }}>
                                                            {meta.icon}
                                                            <span>{tpl.category}</span>
                                                            <span style={{ color: '#6b7280' }}>· {tpl.overlays.length} layer{tpl.overlays.length !== 1 ? 's' : ''}</span>
                                                        </div>
                                                    </div>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* ── SAVED TAB ── */}
                            {activeTab === 'saved' && (
                                <div style={{ padding: '16px' }}>
                                    {/* Save current overlays */}
                                    {overlays.length > 0 && (
                                        <div style={{
                                            padding: '12px', borderRadius: '10px',
                                            background: 'rgba(99,102,241,0.1)',
                                            border: '1px solid rgba(99,102,241,0.3)',
                                            marginBottom: '16px',
                                        }}>
                                            <p style={sectionLabel}>Save Current Overlays as Template</p>
                                            <div style={{ display: 'flex', gap: '8px' }}>
                                                <input
                                                    value={saveName}
                                                    onChange={(e) => setSaveName(e.target.value)}
                                                    placeholder="Template name…"
                                                    style={{
                                                        flex: 1, padding: '8px 10px', borderRadius: '7px', fontSize: '13px',
                                                        background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)',
                                                        color: '#fff', outline: 'none',
                                                    }}
                                                />
                                                <button
                                                    onClick={saveCurrentAsTemplate}
                                                    style={{
                                                        padding: '8px 14px', borderRadius: '7px', fontSize: '13px', fontWeight: 600,
                                                        background: '#6366f1', border: 'none', cursor: 'pointer', color: '#fff',
                                                        display: 'flex', alignItems: 'center', gap: '5px',
                                                    }}
                                                >
                                                    <Save size={14} /> Save
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    {/* Import */}
                                    <button
                                        onClick={importOStudio}
                                        style={{
                                            width: '100%', padding: '10px', borderRadius: '8px', marginBottom: '14px',
                                            background: 'rgba(255,255,255,0.05)', border: '1px dashed rgba(255,255,255,0.2)',
                                            color: '#9ca3af', cursor: 'pointer', fontSize: '13px',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                                        }}
                                    >
                                        <FolderOpen size={15} /> Import .ostudio file
                                    </button>

                                    {savedTemplates.length === 0 ? (
                                        <p style={{ color: '#6b7280', fontSize: '13px', textAlign: 'center', padding: '24px 0' }}>
                                            No saved templates yet.<br />Add overlays then save them above.
                                        </p>
                                    ) : (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                            {savedTemplates.map((file, idx) => (
                                                <div
                                                    key={idx}
                                                    style={{
                                                        display: 'flex', alignItems: 'center', gap: '10px',
                                                        padding: '10px 12px', borderRadius: '8px',
                                                        background: 'rgba(255,255,255,0.05)',
                                                        border: '1px solid rgba(255,255,255,0.08)',
                                                    }}
                                                >
                                                    <div style={{ flex: 1, minWidth: 0 }}>
                                                        <p style={{ margin: 0, fontSize: '13px', fontWeight: 600, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                            {file.name}
                                                        </p>
                                                        <p style={{ margin: '2px 0 0', fontSize: '11px', color: '#6b7280' }}>
                                                            {file.overlays.length} layer{file.overlays.length !== 1 ? 's' : ''} · {new Date(file.createdAt).toLocaleDateString()}
                                                        </p>
                                                    </div>
                                                    <button
                                                        onClick={() => applySavedTemplate(file)}
                                                        style={{ padding: '5px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: 600, background: '#6366f1', border: 'none', cursor: 'pointer', color: '#fff' }}
                                                    >
                                                        Apply
                                                    </button>
                                                    <button
                                                        onClick={() => exportTemplate(file)}
                                                        style={{ padding: '5px', borderRadius: '6px', background: 'rgba(255,255,255,0.08)', border: 'none', cursor: 'pointer', color: '#9ca3af', display: 'flex' }}
                                                        title="Export as .ostudio"
                                                    >
                                                        <Save size={13} />
                                                    </button>
                                                    <button
                                                        onClick={() => deleteSaved(idx)}
                                                        style={{ padding: '5px', borderRadius: '6px', background: 'rgba(239,68,68,0.15)', border: 'none', cursor: 'pointer', color: '#f87171', display: 'flex' }}
                                                        title="Delete"
                                                    >
                                                        <X size={13} />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* ── Footer ── */}
                        <div style={{
                            padding: '10px 16px', borderTop: '1px solid rgba(99,102,241,0.2)',
                            fontSize: '11px', color: '#6b7280', textAlign: 'center', flexShrink: 0,
                        }}>
                            Click a template to apply it · Drag header to reposition
                        </div>
                    </div>
                </>,
                document.body
            )}
        </div>
    );
};
